/**
 * UI-agnostic backend contract for calendar reconcile & feed-token endpoints.
 *
 * These builders define the exact server URLs and the set of doc ids that must
 * be synced. They are invisible to any UI redesign and MUST stay byte-stable —
 * a regression here silently breaks sync. Pinned by characterization tests.
 */

import {
  collectDatabaseStorageSyncDocIds,
  type DatabaseShardDirectoryHost,
  readDatabaseShardDirectoryDocIds,
} from '@streamlain/database-identity';

/** Minimal addressing context for a workspace database doc. */
export interface CalendarReconcileTarget {
  databaseId: string;
  docId: string;
  workspaceId: string;
}

/** POST endpoint that reconciles a single database row. */
export function calendarReconcileUrl(
  target: CalendarReconcileTarget,
  rowId: string
): string {
  return [
    '/api/workspace',
    encodeURIComponent(target.workspaceId),
    'calendar/docs',
    encodeURIComponent(target.docId),
    'databases',
    encodeURIComponent(target.databaseId),
    'rows',
    encodeURIComponent(rowId),
    'reconcile',
  ].join('/');
}

/** Endpoint that issues the shareable ICS feed token for a database. */
export function calendarFeedTokenUrl(target: CalendarReconcileTarget): string {
  return [
    '/api/workspace',
    encodeURIComponent(target.workspaceId),
    'calendar/docs',
    encodeURIComponent(target.docId),
    'databases',
    encodeURIComponent(target.databaseId),
    'feed-token',
  ].join('/');
}

/** Resource-scoped Calendar<->Mailbox link endpoint. */
export function calendarMailboxLinksUrl(
  target: Pick<
    CalendarReconcileTarget,
    'databaseId' | 'docId' | 'workspaceId'
  > & { viewId?: string }
): string {
  return [
    '/api/workspace',
    encodeURIComponent(target.workspaceId),
    'calendar/docs',
    encodeURIComponent(target.docId),
    'databases',
    encodeURIComponent(target.databaseId),
    'views',
    encodeURIComponent(target.viewId || 'calendar'),
    'mailbox-links',
  ].join('/');
}

/** De-duplicated, falsy-filtered set of doc ids that must be synced. */
export function collectCalendarReconcileDocIds(input: {
  databaseId: string;
  databaseRootDocId?: string;
  docId: string;
  /** Database block model storing the directory; parts in its workspace. */
  storageModel?: DatabaseShardDirectoryHost;
  workspaceId: string;
}): string[] {
  // Directory parts and the row shards of the loaded leading parts.
  const directory = readDatabaseShardDirectoryDocIds(input.storageModel);
  return [
    ...new Set(
      [
        input.workspaceId,
        input.docId,
        ...collectDatabaseStorageSyncDocIds({
          databaseId: input.databaseId,
          databaseRootDocId: input.databaseRootDocId,
          databaseDirectoryPartDocIds: directory?.partDocIds,
          databaseRowShardDocIds: directory?.shardDocIds,
          workspaceId: input.workspaceId,
        }),
      ].filter((docId): docId is string => Boolean(docId))
    ),
  ];
}
